import { createFileRoute, Link } from "@tanstack/react-router";
import { useAdminEmailDetail } from "@/hooks/use-admin-emails";
import { StatusBadge } from "@/components/status-badge";
import { EventTimeline } from "@/components/event-timeline";
import { formatDateTime } from "@njbsoft/utils";

export const Route = createFileRoute("/_admin/emails/$emailId")({
  component: EmailDetailPage,
});

function EmailDetailPage() {
  const { emailId } = Route.useParams();
  const { data: email, isLoading, error } = useAdminEmailDetail(emailId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-24 animate-pulse rounded bg-slate-200" />
        <div className="h-48 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        Failed to load email: {error.message}
      </div>
    );
  }

  if (!email) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-slate-600">
        Email not found.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Link
        to="/emails"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
          />
        </svg>
        Back to Email Logs
      </Link>

      {/* Email Metadata Card */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{email.subject}</h1>
            <p className="mt-1 text-sm text-slate-500">To: {email.recipient_email}</p>
          </div>
          <StatusBadge status={email.status} type="email" />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <span className="font-medium text-slate-500">Template</span>
            <p className="text-slate-700">{email.template_name}</p>
          </div>
          <div>
            <span className="font-medium text-slate-500">Category</span>
            <p className="capitalize text-slate-700">{email.category}</p>
          </div>
          <div>
            <span className="font-medium text-slate-500">Created</span>
            <p className="text-slate-700">{formatDateTime(email.created_at)}</p>
          </div>
          {email.resend_id && (
            <div>
              <span className="font-medium text-slate-500">Resend ID</span>
              <p className="font-mono text-xs text-slate-700">{email.resend_id}</p>
            </div>
          )}
        </div>
      </div>

      {/* Event Timeline */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-slate-700">Event Timeline</h2>
        <EventTimeline events={email.events} />
      </section>
    </div>
  );
}
